import React from 'react';
import {
  Box, Button, Grid, IconButton, Paper, Stack, Typography, useTheme, useMediaQuery,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { NumericTextField, SaveIndicator } from '@personal-dashboard/ui';

interface SetItem {
  reps: string | number;
  weight: string | number;
}

interface SetEditorProps {
  sets: SetItem[];
  onUpdateSet: (index: number, key: string, val: any) => void;
  onAddSet: () => void;
  onDeleteSet: (index: number) => void;
  onSave: () => void;
  editingLogId: string | null;
  onCancel: () => void;
  saveStatus: 'idle' | 'saving' | 'saved' | 'error';
}

export const SetEditor: React.FC<SetEditorProps> = ({
  sets, onUpdateSet, onAddSet, onDeleteSet, onSave, editingLogId, onCancel, saveStatus,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Stack spacing={1} sx={{ mt: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'bold' }}>
          Sets
        </Typography>
        <SaveIndicator status={saveStatus} />
      </Box>

      {sets.map((set, idx) => (
        <Paper key={idx} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
          <Grid container spacing={1.5} alignItems="center">
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                  Set {idx + 1}
                </Typography>
                <IconButton
                  size={isMobile ? 'medium' : 'small'}
                  color="error"
                  onClick={() => onDeleteSet(idx)}
                  aria-label={`Delete set ${idx + 1}`}
                  edge="end"
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <NumericTextField
                label="Reps"
                value={set.reps}
                onChange={(val) => onUpdateSet(idx, 'reps', val)}
                size="small"
                fullWidth
              />
            </Grid>
            <Grid item xs={6}>
              <NumericTextField
                label="Weight (kg)"
                value={set.weight}
                onChange={(val) => onUpdateSet(idx, 'weight', val)}
                allowDecimal
                size="small"
                fullWidth
              />
            </Grid>
          </Grid>
        </Paper>
      ))}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ pt: 1 }}>
        <Button
          startIcon={<AddIcon />}
          size="medium"
          onClick={onAddSet}
          fullWidth={isMobile}
          sx={isMobile ? { minHeight: 48 } : undefined}
        >
          Add Set
        </Button>
        <Button
          variant="contained"
          size="medium"
          onClick={onSave}
          fullWidth={isMobile}
          sx={isMobile ? { minHeight: 48 } : undefined}
        >
          Save Log
        </Button>
        {editingLogId && (
          <Button
            size="medium"
            variant="outlined"
            color="secondary"
            onClick={onCancel}
            fullWidth={isMobile}
            sx={isMobile ? { minHeight: 48 } : undefined}
          >
            Cancel
          </Button>
        )}
      </Stack>
    </Stack>
  );
};
